import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */

/** API */
import { fileUrl } from 'api/api'
import PersonnelApi from 'api/setting/personnel-managements/personnel.api'
import PositionApi from 'api/setting/personnel-managements/position.api'
import { routeName } from 'routes/routes-name'

/** UTILS || SLICE */
import { selectMe } from 'app/slice/user.slice'
import { imgs } from 'constants/images'
import { PACKAGE_TYPE, userLimit } from 'constants/package'
import { colors } from 'constants/theme'
import { getPackageKey } from 'utils/app.utils'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Avatar, Box, Grid, InputAdornment, Typography } from '@mui/material'
import { swalWarning } from 'component/Alert/Swal'
import HeaderCard from 'component/CardCustom/HeaderCard'
import Loading from 'component/Loading'
import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import { notiError } from 'component/notifications/notifications'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import { dateTimeToView } from 'utils/date.utils'
import { exportPersonnaelToExcel } from './exportPersonnaelToExcel'

export default function Personnels() {

  const permissions = {
    isCreate: isCreate(),
    isDelete: isDelete(),
    isUpdate: isUpdate(),
    isView: isView()
  }
  const componentRef = useRef<any>(null)
  const { t } = useTranslation()
  const history = useHistory()
  const user = useSelector(selectMe)

  /** DATA FILTERS */
  const [positions, setPositions] = useState<any[]>([])
  const [personnels, setPersonnels] = useState([])
  const [exportData, setExportData] = useState([])

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [position, setPosition] = useState<number | 'ALL'>('ALL')
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')
  const [loading, setLoading] = useState(false)

  const loadData = async () => {
    try {
      setLoading(true)
      let condition: any = {}
      if (page) condition = { ...condition, page: page }
      if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
      if (search) condition = { ...condition, search: search }
      if (sortBy) condition = { ...condition, sortBy: sortBy }
      if (sortType) condition = { ...condition, sortType: sortType }
      if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }
      if (position !== 'ALL') condition = { ...condition, position: position }

      const res = await PersonnelApi.findAll(condition)
      // Export uses same pagination to avoid memory issues - export button should trigger separate call
      const resExport = await PersonnelApi.findAll({ ...condition, export: 1, page: 1, pageLimit: 500 })
      if (res.status === 200) {
        setRowCount(res.headers['x-total'])
        setPersonnels(res.data)
        setExportData(resExport.data)
        setLoading(false)
      }
    } catch (error) {
      notiError('ERROR')
    }
  }


  const loadFilters = async () => {
    const respPositions = await PositionApi.findAll({ status: 'ACTIVE' })
    if (respPositions.status === 200) {
      setPositions(respPositions.data)
    }
  }


  useEffect(() => {
    loadFilters()
  }, [page, pageLimit, sortBy, sortType])

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const onCreate = async () => {
    if (!checkIsUltraPackage()) {
      checkUserLimit(
        t('PERSONNEL.MESSAGE.LIMIT_PACKAGE.TITLE'),
        t('PERSONNEL.MESSAGE.LIMIT_PACKAGE.SUBTITLE', {
          package: getPackageKey(),
          number: userLimit(getPackageKey())
        }), () => { return }, () => { history.push(routeName.personnels + '/create') })
    } else history.push(routeName.personnels + '/create')
  }

  const onActive = async () => {
    if (user?.userId === id) {
      setIsOpenAlertModal(false)
      setIsOpenSnack(true)
      setTypeSnack("error")
      setLabelSnack(t('USER.ALERT.ID_DUPICATE'))
    } else {
      await PersonnelApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (!checkIsUltraPackage() && statusAlertModal === 'ACTIVE') {
          return checkUserLimit(
            t('PERSONNEL.MESSAGE.LIMIT_PACKAGE.TITLE'),
            t('PERSONNEL.MESSAGE.LIMIT_PACKAGE.SUBTITLE', {
              package: getPackageKey(),
              number: userLimit(getPackageKey())
            }), () => { return }, () => {
              setIsOpenAlertModal(false)
              setIsOpenSnack(true)
              setTypeSnack("success")
              setLabelSnack(t('USER.MESSAGE.SUCCESS.UPDATE_STATUS'))
              loadData()
            })
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('USER.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        }
      })
    }
  }

  const onDelete = async () => {
    if (id) {
      await PersonnelApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('USER.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('USER.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => history.push(routeName.personnels + '/update', data)

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => setPage(val)

  const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

  const handleChangeStatus = (statusVal: string) => setStatus(statusVal)

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'img', disablePadding: false, label: t('PERSONNEL.TABLE.CELL.PERSONNEL_NAME') },
    { id: 'personnelPhone', disablePadding: false, label: t('PERSONNEL.TABLE.CELL.PERSONNEL_PHONE') },
    { id: 'personnelPosition', disablePadding: false, label: t('PERSONNEL.TABLE.CELL.PERSONNEL_POSITION') },
    { id: 'personnelRole', disablePadding: false, label: t('PERSONNEL.TABLE.CELL.PERSONNEL_ROLE') },
    { id: 'personnelBranch', disablePadding: false, label: t('PERSONNEL.TABLE.CELL.PERSONNEL_BRANCH') },
    { id: 'status', disablePadding: false, label: t('USER.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('USER.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number, valueExportData: boolean) => {
    const num = valueExportData === true ? no + 1 : page * pageLimit - pageLimit + no + 1
    const { userId, profile, firstname, lastname, phone, roles, position: personalPosition, updatedBy, updatedAt, status: personalStatus, isAdmin } = objData
    const statusBtnActive = personalStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = personalStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const renderProfile = (personalProfile: any, personalFirstName: string, isAdminStatus: boolean) => {
      let path: any = ''
      if (personalProfile?.fileName) {
        const { fileName, filePath } = personalProfile
        path = `${fileUrl}/${filePath || ''}/${fileName || ''}`
      }
      return (
        <div className="d-flex align-items-center">
          {
            !valueExportData &&
            <Avatar className="mx-0 mr-2" src={path} sx={{ backgroundColor: colors.white }}>
              <img src={imgs.defaultAvatar} className="w-100 h-100" alt="" />
            </Avatar>
          }
          <span>{personalFirstName} {lastname} {isAdminStatus && <sup className='text-nowrap'>({t('USER.TABLE.ADMIN')})</sup> || <></>}</span>
        </div>
      )
    }
    const objRenderData = {
      key: userId,
      id: userId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: num },
        { option: 'COMPONENT', align: 'left', component: renderProfile(profile, firstname, isAdmin === '1' ? true : false), width: 'auto' },
        { option: 'TEXT', align: 'left', label: phone || '-' },
        { option: 'TEXT', align: 'left', label: personalPosition?.positionName },
        { option: 'TEXT', align: 'left', label: roles?.roleName },
        { option: 'TEXT', align: 'left', label: roles?.branchName },
        { option: 'STATUS', align: 'center', label: personalStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy || '-', updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <>
        {valueExportData === true && <TableRowExport {...objRenderData} /> ||
          <TableRowCommon
            {...objRenderData}
            onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
            oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
            onedit={() => onEdit(objRenderData.obj)}
            ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
          />
        }
      </>
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(`${obj.firstname} ${obj.lastname}`)
  }

  const filter = [
    { label: `${t('INPUT.SEARCH')}: ${search || '-'}` },
    { label: `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}` },
    { label: `${t('PERSONNEL.TABLE.CELL.PERSONNEL_POSITION')}: ${position === 'ALL' ? t(`STATUS.ALL`) : positions.find((p) => p.positionId === position)?.positionName}` },
  ]

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`PERSONNEL.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`PERSONNEL.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { name: name, status: t(`PERSONNEL.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('PERSONNEL.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('PERSONNEL.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('PERSONNEL.TABLE.CELL.PERSONNEL_POSITION')}
              value={position}
              onChange={(event) => {
                setPosition(event.target.value)
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                ...positions.map((positionItem: any) => ({
                  value: positionItem.positionId,
                  name: positionItem.positionName
                }))
              ]}
              width="100%"
            />
          </Grid>
          <Grid item xs={2} sm={3} md={1} xl={1}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type='primary'
              variant='contained'
              style={{ width: '120px' }}
              onClick={() => {
                setPage(1)
                loadData()
              }} />
          </Grid>
          <Grid item xs={10} sm={4} md={12} xl={5} display='flex' justifyContent="flex-end">
            <Box display="flex" flexDirection="row" alignItems="center" gap="16px">
              <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                if (e === 'Excel') {
                  const report: any = {
                    title: t('PERSONNEL.TITLE'),
                    statusFilter: `${filter[1].label}  ${filter[2].label}`,
                    rows: exportData.map((item: any) => ({
                      name: `${item?.firstname || item?.firstnameEn} ${item?.lastname || item?.lastnameEn} ${item?.isAdmin === '1' ? `(${t('USER.TABLE.ADMIN')})` : ''}`,
                      phone: item?.phone || '-',
                      positionName: item?.position?.positionName || item?.position?.positionNameEn || '-',
                      roleName: item?.roles?.roleName || item?.roles?.roleNameEn || '-',
                      branchName: item?.roles?.branchName || item?.roles?.branchNameEn || '-',
                      status: t(`STATUS.${item?.status}`),
                      updatedBy: item?.updatedBy,
                      updatedAt: dateTimeToView(item?.updatedAt) || '-'
                    })),
                    t
                  }
                  exportPersonnaelToExcel(report)
                } else if (e === 'PDF') {
                  const style = document.createElement("style");
                  style.innerHTML = `
                                  @media print {
                                    @page { size: landscape }
                                  }
                                `;
                  document.head.appendChild(style);
                  window.print();
                  document.head.removeChild(style);
                }
              }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
              <MainButton title={t('PERSONNEL.BUTTON.ADD')} disabled={permissions.isCreate.disabled} type='primary' variant='contained' onClick={() => onCreate()} startIcon={<AddCircleIcon />} />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={personnels.map((val, i) => {
              return renderData(val, i, false)
            })}
            tableMinWidth={1440}
          />
        </Box>
      </Box>
      <Loading show={loading} type='fullLoading' />

      <Box
        id="print-table-container"
        ref={componentRef}
      >
        <Typography fontSize="14px" fontWeight={400}>{t('PERSONNEL.TITLE')}</Typography>
        <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
          <Typography fontSize="10px" fontWeight={400}>{t('STATUS.LABEL')}: {status === 'ALL' ? t('STATUS.ALL') : status === 'ACTIVE' ? t('STATUS.ACTIVE') : t('STATUS.INACTIVE')}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{t('PERSONNEL.TABLE.CELL.PERSONNEL_POSITION')}: {positions.find((item) => item.positionId === position)?.positionName || 'ทั้งหมด'}</Typography>
        </Box>
        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
          <thead>
            <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ borderBottom: '1px solid black' }}>{t('#')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PERSONNEL.TABLE.CELL.PERSONNEL_NAME')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PERSONNEL.TABLE.CELL.PERSONNEL_PHONE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PERSONNEL.TABLE.CELL.PERSONNEL_POSITION')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PERSONNEL.TABLE.CELL.PERSONNEL_ROLE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PERSONNEL.TABLE.CELL.PERSONNEL_ROLE')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('USER.TABLE.CELL.STATUS')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('USER.TABLE.CELL.UPDATED_BY')}</th>
            </tr>
          </thead>
          <tbody>
            {exportData?.length === 0 &&
              <tr>
                <td colSpan={8} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
              </tr>
            }
            {exportData.map((data: any, index: number) => (
              <tr key={index}>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '200px' }}>{data?.firstname || '-'}{data?.lastname || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '85px' }}>{data?.phone || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '100px' }}>{data?.position?.positionName || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '150px' }}>{data?.roles?.roleName || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.roles?.branchName || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100px' }}>{t(`STATUS.${data?.status}`) || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <span>{data?.updatedBy || '-'}</span>
                  <span>{dateTimeToView(data?.updatedAt) || '-'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  )
}

export const checkUserLimit = async (title: string, subtitle: string, handleIsOverCase: () => void, handleIsNotOverCase: () => void) => {
  const user = await getUserLimit()
  if ((Number(user) + 1) > userLimit(getPackageKey())) {
    warningCreateUser(title, subtitle, () => { handleIsOverCase() })
  } else handleIsNotOverCase()
}

export const checkIsUltraPackage = () => {
  return getPackageKey() === PACKAGE_TYPE.ULTRA ? true : false
}

export const getUserLimit = async () => {
  let user = 0
  await PersonnelApi.findUserActive().then((res) => user = res.data)
  return user
}

export const warningCreateUser = (title: string, subtitle: string, callback?: any) => {
  return swalWarning(title, subtitle, callback, { allowOutsideClick: false })
}
